const VERSION = 5;
const SIZE = VERSION * 4 + 17;
const DATA_CODEWORDS = 108;
const ECC_CODEWORDS = 26;

const EXP = new Array(512);
const LOG = new Array(256);
let gfReady = false;

function initGf() {
    if (gfReady) return;
    let x = 1;
    for (let i = 0; i < 255; i += 1) {
        EXP[i] = x;
        LOG[x] = i;
        x <<= 1;
        if (x & 0x100) x ^= 0x11d;
    }
    for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
    gfReady = true;
}

function gfMul(a, b) {
    if (!a || !b) return 0;
    initGf();
    return EXP[LOG[a] + LOG[b]];
}

function rsGenerator(degree) {
    initGf();
    let result = [1];
    for (let i = 0; i < degree; i += 1) {
        const next = new Array(result.length + 1).fill(0);
        result.forEach((coef, index) => {
            next[index] ^= gfMul(coef, EXP[i]);
            next[index + 1] ^= coef;
        });
        result = next;
    }
    return result;
}

function rsRemainder(data, degree) {
    // rsGenerator is lowest-degree first with a leading 1 last; the division below needs
    // the other coefficients highest-degree first.
    const gen = rsGenerator(degree).reverse().slice(1);
    const result = new Array(degree).fill(0);
    data.forEach((byte) => {
        const factor = byte ^ result.shift();
        result.push(0);
        gen.forEach((coef, index) => {
            result[index] ^= gfMul(coef, factor);
        });
    });
    return result;
}

function appendBits(bits, value, length) {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
}

function encodeBytes(text) {
    const bytes = [];
    Array.from(String(text || '')).forEach((char) => {
        const code = char.codePointAt(0);
        if (code <= 0x7f) bytes.push(code);
        else if (code <= 0x7ff) bytes.push(0xc0 | (code >>> 6), 0x80 | (code & 0x3f));
        else if (code <= 0xffff) bytes.push(0xe0 | (code >>> 12), 0x80 | ((code >>> 6) & 0x3f), 0x80 | (code & 0x3f));
        else bytes.push(0xf0 | (code >>> 18), 0x80 | ((code >>> 12) & 0x3f), 0x80 | ((code >>> 6) & 0x3f), 0x80 | (code & 0x3f));
    });
    if (bytes.length > 106) {
        throw new Error('QR URL is too long for the built-in encoder.');
    }
    const bits = [];
    appendBits(bits, 0x4, 4);
    appendBits(bits, bytes.length, 8);
    bytes.forEach((byte) => appendBits(bits, byte, 8));
    appendBits(bits, 0, Math.min(4, DATA_CODEWORDS * 8 - bits.length));
    while (bits.length % 8) bits.push(0);
    const data = [];
    for (let i = 0; i < bits.length; i += 8) {
        data.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
    }
    for (let pad = 0xec; data.length < DATA_CODEWORDS; pad ^= 0xfd) data.push(pad);
    return data;
}

function emptyMatrix() {
    return {
        modules: Array.from({ length: SIZE }, () => new Array(SIZE).fill(false)),
        reserved: Array.from({ length: SIZE }, () => new Array(SIZE).fill(false)),
    };
}

function setCell(qr, row, col, value, reserve = true) {
    if (row < 0 || col < 0 || row >= SIZE || col >= SIZE) return;
    qr.modules[row][col] = Boolean(value);
    if (reserve) qr.reserved[row][col] = true;
}

function drawFinder(qr, row, col) {
    for (let dy = -4; dy <= 4; dy += 1) {
        for (let dx = -4; dx <= 4; dx += 1) {
            const r = row + dy;
            const c = col + dx;
            const dist = Math.max(Math.abs(dx), Math.abs(dy));
            setCell(qr, r, c, dist !== 2 && dist !== 4);
        }
    }
}

function drawAlignment(qr, row, col) {
    for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
            const dist = Math.max(Math.abs(dx), Math.abs(dy));
            setCell(qr, row + dy, col + dx, dist !== 1);
        }
    }
}

function drawFunctionPatterns(qr) {
    // Timing first: the finders drawn after it must keep their solid borders.
    for (let i = 0; i < SIZE; i += 1) {
        setCell(qr, 6, i, i % 2 === 0);
        setCell(qr, i, 6, i % 2 === 0);
    }
    drawFinder(qr, 3, 3);
    drawFinder(qr, 3, SIZE - 4);
    drawFinder(qr, SIZE - 4, 3);
    drawAlignment(qr, 30, 30);
    // Reserves the format areas (and sets the dark module) before data placement.
    drawFormatBits(qr);
}

function formatBits() {
    let data = (1 << 3) | 0; // ECC L, mask 0
    let rem = data << 10;
    const poly = 0x537;
    for (let i = 14; i >= 10; i -= 1) {
        if ((rem >>> i) & 1) rem ^= poly << (i - 10);
    }
    return (((data << 10) | rem) ^ 0x5412) & 0x7fff;
}

// Positions per ISO/IEC 18004 (as in Nayuki's reference encoder), as setCell(row, col).
function drawFormatBits(qr) {
    const bits = formatBits();
    const bit = (i) => ((bits >>> i) & 1) === 1;
    // First copy, around the top-left finder.
    for (let i = 0; i <= 5; i += 1) setCell(qr, i, 8, bit(i));
    setCell(qr, 7, 8, bit(6));
    setCell(qr, 8, 8, bit(7));
    setCell(qr, 8, 7, bit(8));
    for (let i = 9; i < 15; i += 1) setCell(qr, 8, 14 - i, bit(i));
    // Second copy, split between the top-right and bottom-left finders.
    for (let i = 0; i < 8; i += 1) setCell(qr, 8, SIZE - 1 - i, bit(i));
    for (let i = 8; i < 15; i += 1) setCell(qr, SIZE - 15 + i, 8, bit(i));
    setCell(qr, SIZE - 8, 8, true); // always-dark module
}

function placeData(qr, bytes) {
    const bits = [];
    bytes.forEach((byte) => appendBits(bits, byte, 8));
    let index = 0;
    let upward = true;
    for (let right = SIZE - 1; right >= 1; right -= 2) {
        if (right === 6) right -= 1;
        for (let vert = 0; vert < SIZE; vert += 1) {
            const row = upward ? SIZE - 1 - vert : vert;
            for (let j = 0; j < 2; j += 1) {
                const col = right - j;
                if (qr.reserved[row][col]) continue;
                const masked = (bits[index] || 0) ^ (((row + col) % 2 === 0) ? 1 : 0);
                setCell(qr, row, col, masked, false);
                index += 1;
            }
        }
        upward = !upward;
    }
}

export function createQrSvg(value, { scale = 6, border = 4 } = {}) {
    const data = encodeBytes(value);
    const bytes = [...data, ...rsRemainder(data, ECC_CODEWORDS)];
    const qr = emptyMatrix();
    drawFunctionPatterns(qr);
    placeData(qr, bytes);
    drawFormatBits(qr);
    const outer = (SIZE + border * 2) * scale;
    const rects = [];
    qr.modules.forEach((row, r) => row.forEach((dark, c) => {
        if (dark) rects.push(`<rect x="${(c + border) * scale}" y="${(r + border) * scale}" width="${scale}" height="${scale}"/>`);
    }));
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${outer}" height="${outer}" viewBox="0 0 ${outer} ${outer}" role="img" aria-label="Shop referral QR code"><rect width="100%" height="100%" fill="#fff"/><g fill="#000">${rects.join('')}</g></svg>`;
}

export function createQrDataUrl(value, options) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(createQrSvg(value, options))}`;
}

export async function downloadQrPng(value, filename = 'carfido-shop-referral-qr.png') {
    const svg = createQrSvg(value, { scale: 12, border: 4 });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    try {
        const image = new Image();
        const loaded = new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
        });
        image.src = url;
        await loaded;
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = filename;
        link.click();
    } finally {
        URL.revokeObjectURL(url);
    }
}
